import { useState, Fragment } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { clsx } from 'clsx'
import {
  HomeIcon,
  Cog6ToothIcon,
  ChartBarIcon,
  BuildingOffice2Icon,
  EnvelopeIcon,
  ChatBubbleLeftEllipsisIcon,
  UserGroupIcon,
  PaintBrushIcon,
  ShieldCheckIcon,
  DevicePhoneMobileIcon,
  MapIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  CreditCardIcon,
  PhoneIcon,
  DocumentTextIcon,
  ShieldCheckIcon as PolicyIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline'
import { useAppStore } from '../../store/useAppStore'
import type { ServiceType } from '../../types/database'

interface SidebarProps {
  className?: string
}

const navigation = [
  { name: 'Dashboard', icon: HomeIcon, view: 'dashboard' as const },
  { name: 'Services', icon: Cog6ToothIcon, view: 'services' as const },
  { name: 'Pricing', icon: CreditCardIcon, view: 'pricing' as const },
  { name: 'Analytics', icon: ChartBarIcon, view: 'analytics' as const },
  { name: 'Contacts', icon: PhoneIcon, view: 'contacts' as const },
  { name: 'Settings', icon: Cog6ToothIcon, view: 'settings' as const },
]

const legalNav = [
  { name: 'About Us', icon: InformationCircleIcon, view: 'about' as const },
  { name: 'Terms of Service', icon: DocumentTextIcon, view: 'terms' as const },
  { name: 'Privacy Policy', icon: PolicyIcon, view: 'privacy' as const },
]

const services: { name: string; type: ServiceType; icon: any }[] = [
  { name: 'Enterprise Intelligence', type: 'enterprise_intelligence', icon: ChartBarIcon },
  { name: 'Web Design', type: 'web_design', icon: PaintBrushIcon },
  { name: 'Marketing', type: 'marketing', icon: EnvelopeIcon },
  { name: 'AI Assistant', type: 'ai_assistant', icon: ChatBubbleLeftEllipsisIcon },
  { name: 'CRM', type: 'crm', icon: UserGroupIcon },
  { name: 'Branding', type: 'branding', icon: PaintBrushIcon },
  { name: 'Vulnerability Assessment', type: 'vulnerability_assessment', icon: ShieldCheckIcon },
  { name: 'Mobile App Dev', type: 'mobile_app', icon: DevicePhoneMobileIcon },
  { name: 'Google Maps', type: 'google_maps', icon: MapIcon },
]

export const Sidebar = ({ className }: SidebarProps) => {
  const [mobileOpen, setMobileOpen] = useState(false)
  const {
    sidebarOpen,
    toggleSidebar,
    activeView,
    setActiveView,
    activeServiceType,
    setActiveServiceType,
    logout,
    user,
  } = useAppStore()

  const handleViewChange = (view: typeof activeView) => {
    setActiveView(view)
    if (view !== 'services') {
      setActiveServiceType(null)
    }
  }

  const handleServiceSelect = (type: ServiceType) => {
    setActiveServiceType(type)
    setActiveView('services')
  }

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center gap-3 px-6 py-5 border-b border-metallic-100">
        <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl flex items-center justify-center">
          <span className="text-white font-bold text-lg">IC</span>
        </div>
        <div className="flex-1">
          <h1 className="font-bold text-metallic-900 text-lg leading-tight">IntelCore</h1>
          <p className="text-xs text-metallic-500">Enterprise Intelligence</p>
        </div>
        <button
          onClick={toggleSidebar}
          className="hidden lg:block p-2 text-metallic-400 hover:text-metallic-600 hover:bg-metallic-100 rounded-lg transition-colors"
        >
          <Bars3Icon className="w-5 h-5" />
        </button>
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
        <p className="px-3 text-xs font-semibold text-metallic-400 uppercase tracking-wider mb-2">
          Main Menu
        </p>
        {navigation.map((item) => (
          <button
            key={item.name}
            onClick={() => handleViewChange(item.view)}
            className={clsx(
              'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200',
              activeView === item.view && !activeServiceType
                ? 'bg-primary-50 text-primary-600 shadow-sm'
                : 'text-metallic-600 hover:text-primary-600 hover:bg-primary-50'
            )}
          >
            <item.icon className="w-5 h-5" />
            {item.name}
          </button>
        ))}

        {/* Services Section */}
        <div className="pt-4">
          <p className="px-3 text-xs font-semibold text-metallic-400 uppercase tracking-wider mb-2">
            Services
          </p>
          {services.map((service) => (
            <button
              key={service.type}
              onClick={() => handleServiceSelect(service.type)}
              className={clsx(
                'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200',
                activeServiceType === service.type
                  ? 'bg-primary-50 text-primary-600 shadow-sm'
                  : 'text-metallic-600 hover:text-primary-600 hover:bg-primary-50'
              )}
            >
              <service.icon className="w-5 h-5" />
              {service.name}
            </button>
          ))}
        </div>

        {/* Legal Section */}
        <div className="pt-4">
          <p className="px-3 text-xs font-semibold text-metallic-400 uppercase tracking-wider mb-2">
            Company
          </p>
          {legalNav.map((item) => (
            <button
              key={item.name}
              onClick={() => handleViewChange(item.view)}
              className={clsx(
                'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200',
                activeView === item.view && !activeServiceType
                  ? 'bg-primary-50 text-primary-600 shadow-sm'
                  : 'text-metallic-600 hover:text-primary-600 hover:bg-primary-50'
              )}
            >
              <item.icon className="w-5 h-5" />
              {item.name}
            </button>
          ))}
        </div>
      </nav>

      {/* User Section */}
      <div className="p-4 border-t border-metallic-100">
        <div className="flex items-center gap-3 px-3 py-2">
          <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center">
            <span className="text-white font-semibold text-sm">
              {user?.full_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-metallic-900 truncate">
              {user?.full_name || 'User'}
            </p>
            <p className="text-xs text-metallic-500 truncate">{user?.email}</p>
          </div>
          <button
            onClick={logout}
            className="p-2 text-metallic-400 hover:text-error-500 hover:bg-error-50 rounded-lg transition-colors"
            title="Sign out"
          >
            <ArrowRightOnRectangleIcon className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  )

  return (
    <>
      {/* Desktop Sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.aside
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 280, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className={clsx(
              'hidden lg:block fixed left-0 top-0 h-full bg-white border-r border-metallic-100 shadow-metallic z-40 overflow-hidden',
              className
            )}
          >
            <div className="w-[280px] h-full">{sidebarContent}</div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Mobile Sidebar */}
      <div className="lg:hidden">
        <button
          onClick={() => setMobileOpen(true)}
          className="fixed top-4 left-4 z-50 p-2 bg-white rounded-xl shadow-metallic"
        >
          <Bars3Icon className="w-6 h-6 text-metallic-600" />
        </button>

        <AnimatePresence>
          {mobileOpen && (
            <Fragment>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 z-40"
                onClick={() => setMobileOpen(false)}
              />
              <motion.aside
                initial={{ x: -280 }}
                animate={{ x: 0 }}
                exit={{ x: -280 }}
                transition={{ type: 'spring', damping: 25 }}
                className="fixed left-0 top-0 h-full w-[280px] bg-white shadow-metallic-xl z-50"
              >
                <button
                  onClick={() => setMobileOpen(false)}
                  className="absolute top-4 right-4 p-2 text-metallic-400 hover:text-metallic-600 hover:bg-metallic-100 rounded-lg"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
                {sidebarContent}
              </motion.aside>
            </Fragment>
          )}
        </AnimatePresence>
      </div>
    </>
  )
}
